import { FC, ReactElement, useMemo, useState } from 'react';
import {
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonSpinner,
} from '@ionic/react';
import { checkmark, trash } from 'ionicons/icons';
import { StoreInterface } from '../../../../store/types';
import { useSelector } from 'react-redux';
import { PaymentGateway, PaymentMethod, PaymentMethodBrand } from '../../../../services/exports/Constants';
import { Modals } from '../../exports/Enums';
import MasterCardIcon from '../../../../assets/icons/payment-methods/MasterCardIcon';
import VisaIcon from '../../../../assets/icons/payment-methods/VisaIcon';
import AmericanExpressIcon from '../../../../assets/icons/payment-methods/AmericanExpressIcon';
import { collect } from 'collect.js';
import { PaymentOptionPropertiesInterface } from '../../../../services/exports/Interfaces';
import PaymentManager from '../../../../services/api/PaymentManager';
import { IModal } from '../../router/ModalRouter';
import { useTranslation } from 'react-i18next';
import GooglePayIcon from '../../../../assets/icons/payment-methods/GooglePayIcon';
import ApplePayIcon from '../../../../assets/icons/payment-methods/ApplePayIcon';
import PayPalIcon from '../../../../assets/icons/payment-methods/PayPalIcon';
import classnames from 'classnames';
import { normalizeCardBrand } from '../../../../services/helpers/cardBrand';
import { isStripeGooglePayInMobileApp } from '../../../../utils/payment-options-visibility';

interface IPaymentOptionIcon {
  icon: React.ReactNode;
  label: string;
  brand?: PaymentMethodBrand;
}

interface IPaymentOption {
  id: PaymentMethod;
  label: string;
  icons: IPaymentOptionIcon[];
  paymentOption: PaymentOptionPropertiesInterface;
  onClick?: () => void;
}

interface Props {
  openModal: IModal['openModal'];
  /** Whether the Card row offers "add a card" (a Stripe SetupIntent). Off for gateways whose
   * cards can only be saved during checkout, e.g. Spreedly. Defaults to true. */
  canAddCard?: boolean;
}

export default function StripePaymentMethodsList({ openModal, canAddCard = true }: Props) {
  const { t } = useTranslation(null, {
    keyPrefix:
      'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:PaymentMethods:PaymentMethodsModal',
  });
  const { payment_options } = useSelector((state: StoreInterface) => state.paymentOptions);
  const [deletingPaymentOption, setDeletingPaymentOption] = useState<boolean>(false);

  const removePaymentOption = async (id: number): Promise<void> => {
    setDeletingPaymentOption(true);
    await PaymentManager.removePaymentOption(id);
    setDeletingPaymentOption(false);
  };

  const iconClassName = 'h-[14px]';

  const paymentOptions = useMemo(
    () =>
      collect([
        {
          id: PaymentMethod.Card,
          label: t(`payment_options.${PaymentMethod.Card}`),
          icons: [
            {
              icon: <MasterCardIcon className={iconClassName} />,
              label: 'MasterCard',
              brand: PaymentMethodBrand.MasterCard,
            },
            {
              icon: <AmericanExpressIcon className={iconClassName} />,
              label: 'American Express',
              brand: PaymentMethodBrand.AmericanExpress,
            },
            {
              icon: <VisaIcon className={iconClassName} />,
              label: 'Visa',
              brand: PaymentMethodBrand.Visa,
            },
          ],
          onClick: canAddCard ? () => openModal({ id: Modals.AddCreditCard }) : undefined,
        },
        {
          id: PaymentMethod.GooglePay,
          label: 'Google Pay',
          icons: [
            {
              icon: <GooglePayIcon className={classnames(iconClassName, 'h-5')} />,
            },
          ],
        },
        {
          id: PaymentMethod.ApplePay,
          label: 'Apple Pay',
          icons: [
            {
              icon: <ApplePayIcon className={iconClassName} />,
            },
          ],
        },
        {
          id: PaymentMethod.Paypal,
          label: 'Paypal',
          icons: [
            {
              icon: <PayPalIcon className={iconClassName} />,
            },
          ],
        },
      ])
        // This list only renders inside the app, where Stripe Google Pay isn't offered.
        .reject((item) => isStripeGooglePayInMobileApp(item.id, PaymentGateway.Stripe, true))
        .map((item) => {
          const paymentOption = collect(payment_options).firstWhere('payment_method_id', item.id);

          return {
            ...item,
            paymentOption,
            label:
              paymentOption &&
              paymentOption.payment_method_details &&
              paymentOption.payment_method_id === PaymentMethod.Card
                ? `**** **** **** ${paymentOption.payment_method_details}`
                : item.label,
            // Through the normaliser, not a raw `===`: the backend stores `payment_method_brand`
            // as each gateway spells it (Stripe `mastercard`, Spreedly `master`, Adyen
            // `master_card`), so only Adyen's spelling ever matched the enum — a Stripe-saved
            // Mastercard fell through to no icon at all.
            icons: paymentOption
              ? collect<IPaymentOptionIcon>(item.icons)
                  .filter((item) => item?.brand === normalizeCardBrand(paymentOption?.payment_method_brand))
                  .toArray()
              : item.icons,
          };
        })
        .toArray() as IPaymentOption[],
    [payment_options, canAddCard],
  );

  const renderPaymentOption: FC<IPaymentOption> = ({ icons, label, paymentOption, onClick }): ReactElement => {
    const hasPaymentOption = !!paymentOption;

    return (
      <IonItemSliding key={paymentOption?.id}>
        <IonItem
          button={!hasPaymentOption && !!onClick}
          onClick={!hasPaymentOption ? onClick : undefined}
          className="flex justify-between"
        >
          <IonLabel>
            <h6 className="!font-medium">{label}</h6>
          </IonLabel>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {icons.map((item) => (
                <div key={item.label}>{item.icon}</div>
              ))}
            </div>
            {paymentOption?.is_default && <IonIcon slot="end" icon={checkmark} />}
          </div>
        </IonItem>
        <IonItemOptions>
          {hasPaymentOption && (
            <IonItemOption color="danger" onClick={() => removePaymentOption(paymentOption.id)}>
              {deletingPaymentOption ? <IonSpinner name="crescent" className="w-5 h-5" /> : <IonIcon icon={trash} />}
            </IonItemOption>
          )}
        </IonItemOptions>
      </IonItemSliding>
    );
  };

  return <IonList>{paymentOptions.map(renderPaymentOption)}</IonList>;
}
